import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { FormPanelComponent } from './panels/form/form.component';
import { CommentPanelComponent } from './panels/comments/comments.component';
import { ImagesPanelComponent } from './panels/gallery/gallery.component';
import { JupiterCrmDataTablePanelComponent } from './panels/crm-data-table-panel/crm-data-table-panel.component';
import { PanelMatrixComponent } from '@shared/components/panels/matrix/matrix.component';
import { JupiterPanelComponent } from './panels/jupiter-panel.component';

import { PanelTableComponent } from '@shared/components/panels/related-table/related-table.component';
import { AccountState } from '@state/account/state';
import { PanelsModule } from '@shared/components/panels/panels.module';

export const JupiterPanelsMap = {
  form: FormPanelComponent,
  comments: CommentPanelComponent,
  table: PanelTableComponent,
  images: ImagesPanelComponent,
  matrix: PanelMatrixComponent,
  'crm-table-panel': JupiterCrmDataTablePanelComponent,
};

@Component({
  selector: 'app-panel-host',
  standalone: true,
  template: ` <ng-template #panelHost></ng-template> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PanelsModule],
})
export class JupiterPanelHostComponent extends JupiterPanelComponent<any> implements OnInit, OnChanges {
  @ViewChild('panelHost', { static: true, read: ViewContainerRef }) panelHost: ViewContainerRef;

  private component: ComponentRef<JupiterPanelComponent<any>>;

  get componentRef(): ComponentRef<JupiterPanelComponent<any>> {
    return this.component;
  }

  get componentInstance(): JupiterPanelComponent<any> {
    return this.component.instance;
  }

  ngOnInit(): void {
    this.loadComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.model?.firstChange === false) {
      this.component.setInput('model', changes.model.currentValue);
    }
  }

  loadComponent(): void {
    if (!JupiterPanelsMap[this.panelDef.type]) {
      throw Error(`Panel type ${this.panelDef.type} is not supported`);
    }

    const viewContainerRef = this.panelHost;
    viewContainerRef.clear();

    const component = this.panelHost.createComponent<JupiterPanelComponent<any>>(
      JupiterPanelsMap[this.panelDef.type] as any, // as any for compatibility with the old matrix panel component
    );

    component.instance.panelDef = this.panelDef;
    component.instance.model = { ...this.model };
    component.instance.data = this.data;
    component.instance.relatedTo = this.relatedTo;
    component.instance.fieldDefs = this.store.selectSnapshot(ObjectsManagerState.getFieldsByObjectName(this.relatedTo));
    component.instance.panelDefUpdated = this.panelDefUpdated;
    component.instance.dataUpdated = this.dataUpdated;
    component.instance.editToggled = this.editToggled;
    component.instance.discardClicked = this.discardClicked;

    component.instance.touched = new BehaviorSubject(false);
    component.instance.isBusy = new BehaviorSubject(false);
    component.instance.editMode = new BehaviorSubject(false);

    // temporary fix for related table panel
    this.setRelatedTablePanelProps(component.instance);

    this.component = component;
  }

  private setRelatedTablePanelProps(componentInstance: any): void {
    componentInstance.customObjectsOnly = true;
    componentInstance.relatedToType = this.relatedTo;
    componentInstance.readonly = false;
    componentInstance.account = this.store.selectSnapshot(AccountState.getAccount);
  }
}
